import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import { SearchChatsList } from "../../../model/branch/BranchChatsModel";
import BranchDeptChatsProvider from "./BranchDeptChatsProvider";
import { useDispatch } from "react-redux";
import { addModal } from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";
import { updateDeptChat } from "../../../libraries/state_reducers/widget/DetailConnectDeptTeamReducer";
import UtilityHelper from "../../../service/utilities/UtilityHelper";

type BranchDeptChatsContextType = {
  deptChatsList: Array<SearchChatsList> | null;
  branchDeptListInit: () => void;
  onClickDeptChat: (detail: SearchChatsList) => void;

  showSearch: boolean;
  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;
  updateShowSearch: () => void;
};

const BranchDeptChatsContext = createContext<
  BranchDeptChatsContextType | undefined
>(undefined);

export const BranchDeptChatsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  const searchValueRef = useRef<string>("");
  const deptChatsListResultRef = useRef<Array<SearchChatsList> | []>([]);

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [deptChatsList, setDeptChatsList] =
    useState<Array<SearchChatsList> | null>(null);

  const branchDeptListInit = async () => {
    const chatListModel = await BranchDeptChatsProvider.getChatListAPI();
    if (chatListModel != null) {
      const deptChatsListResult: Array<SearchChatsList> | null =
        chatListModel?.result?.search_chats_list!;

      setDeptChatsList(deptChatsListResult);
      deptChatsListResultRef.current = deptChatsListResult;
    }
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-branch-dept`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<SearchChatsList> | undefined =
        deptChatsListResultRef.current?.filter((dept) =>
          Object.values(dept)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setDeptChatsList(filtered!);
    }
  };

  const clearSearch = () => {
    if (UtilityHelper.isEmpty(searchValueRef.current)) {
      updateShowSearch();
    }
    setDeptChatsList(deptChatsListResultRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const updateShowSearch = () => {
    setShowSearch(!showSearch);
  };

  const onClickDeptChat = (detail: SearchChatsList) => {
    dispatch(updateDeptChat(detail));
    dispatch(
      addModal({
        id: ModalKeys.connect_detail_branch_depts_chat,
        title: detail?.chat_title!,
        size: "md",
        allowBottomSheet: true,
        hideHeader: true,
        bottomSheetHideTitle: true,
      })
    );
  };

  return (
    <BranchDeptChatsContext.Provider
      value={{
        deptChatsList,
        branchDeptListInit,
        onClickDeptChat,

        showSearch,
        searchValue,
        handleOnChange,
        clearSearch,
        updateShowSearch,
      }}
    >
      {children}
    </BranchDeptChatsContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const BranchDeptChatsContextImport = () => {
  const context = useContext(BranchDeptChatsContext);
  if (!context) {
    throw new Error(
      "BranchDeptChatsContextImport must be used within a BranchDeptChatsContextProvider"
    );
  }
  return context;
};
